import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Assignment, LocalAtm, Payment } from '@mui/icons-material';
import { Box, Card, Divider, ListItem, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { FuseIcon, ListDataColumn, ListHeader, ListRow } from '/src/components';
import { useCompanyStore, useLoanStore, useStatementStore } from '/src/context';
import { mediumDate, shortDate } from '/src/lib/constants';
import { Loan, TLoanId } from '/src/lib/models';
import { DataModelPluralType } from '/src/lib/types';
import { currency } from '/src/utils';

/**
 * View Statement Page
 */
export const ViewStatementPage = observer(() => {
  const defaultSortType = 'sequenceNumber';
  const defaultSortReverse = true;
  const rowHeight = 48;

  /** Context **/
  const statementStore = useStatementStore();
  const loanStore = useLoanStore();
  const companyStore = useCompanyStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);
  const [loan, setLoan] = useState<Loan>();

  /** Computed **/
  const loanId = params.loanId;
  const month = params.month;

  const statementDate = dayjs(month, 'YYYY-MM');
  const startDate = statementDate.startOf('month');
  const endDate = statementDate.endOf('month');

  const headlines = statementStore.headlines?.[0];
  const transactionHistory = statementStore.transactions;
  const totals = statementStore.totals;
  const beginPrincipalBalance = transactionHistory?.[transactionHistory.length - 1].principalBalance;

  const listData = useMemo(() => {
    if (transactionHistory) {
      const list = transactionHistory.toSorted((a, b) => (get(a, sortType) > get(b, sortType) ? 1 : -1));
      if (sortReverse) list.reverse();

      return list;
    } else return [];
  }, [transactionHistory, sortType, sortReverse]);

  /** Methods **/

  // Changes list sorting
  const sortList = (type: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /**
   * Gets a list of Loan Transactions for the given month.
   *
   * @param loanId ID of the Loan to get transactions for
   * @param month Month string in the format `YYYY-MM`
   */
  const getTxns = async (loanId: TLoanId, month: string) => {
    if (loanId && month) {
      // Make sure the requested statement date is in the past
      if (dayjs(month, 'YYYY-MM').isBefore(dayjs())) {
        const loan = await loanStore.get(loanId);
        if (!loan) return;
        const [borrower] = await Promise.all([
          companyStore.get(loan?.borrower._id),
          statementStore.getLoanStatement(loanId, month),
        ]);
        if (!borrower) return;
        setLoan({
          ...loan,
          borrower: { ...loan.borrower, longName: borrower?.longName, address: borrower?.address },
        });
      } else {
        navigate('/statements');
      }
    }
  };

  /** Effects **/

  useEffect(() => {
    if (loanId && month) getTxns(loanId, month);
  }, [loanId, month]);

  /** Render **/
  return (
    <section className="content">
      <section className="container">
        <Box className="margin-sides-lg pad-bottom">
          <Box display="flex" justifyContent="start" alignItems="top" className="margin-top-md card-margins">
            <Box display="flex" justifyContent="start" alignItems="top" className="pad-top-sm">
              <Box>
                <FuseIcon className="margin-right-xs margin-bottom-xs logo-size" />
              </Box>

              <Box className="thin text-size-md">
                <Box component="span" className="medium">
                  FuSE Financial
                </Box>{' '}
                | 801 K Street, Suite 2700 | Sacramento, CA 95814
                <Divider />
              </Box>
            </Box>

            <Box component="span" display="flex" flex={1}></Box>

            <Box flexDirection="column" display="flex" justifyContent="center" alignItems="end">
              <Box component="span" className="md-headline thin">
                Deal Activity Statement
              </Box>
              <Box component="span" className="md-title-print">
                {startDate?.format('MMMM YYYY')}
              </Box>
            </Box>
          </Box>

          <Box
            display="flex"
            justifyContent="top"
            alignItems="top"
            className="margin-top-lg print-margin-top card-margins"
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="top"
              alignItems="top"
              flex={70}
              className="thin "
            >
              <Box component="span" className="md-headline">
                {loan?.borrower?.longName}
              </Box>
              <Box component="span" className="text-sm">
                {loan?.borrower?.address?.street}
              </Box>
              <Box component="span" className="text-sm">
                {loan?.borrower?.address?.city}, {loan?.borrower?.address?.state}, {loan?.borrower?.address?.zip}
              </Box>
            </Box>

            <Box component="span" display="flex" />

            <Box
              display="flex"
              flex={30}
              flexDirection="column"
              justifyContent="center"
              alignItems="top"
              className="no-margin-right-sm loan-summary-text"
            >
              <ListItem className="height-small" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Deal Name
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {loan?.name}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small medium" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Principal Balance
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(headlines?.principalBalance)}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Fees Due
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(transactionHistory?.[0].feesDue ?? 0)}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Interest Due
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(transactionHistory?.[0].interestDue ?? 0)}
                </Box>
              </ListItem>

              <Divider />

              <ListItem className="height-small" sx={{ height: '35px' }}>
                <Box width="100%" component="span" className="text-size-md">
                  Minimum Payment
                </Box>
                <Box width="100%" component="span" className="text-right">
                  {currency.format(transactionHistory?.[0].minimumPayment ?? 0)}
                </Box>
              </ListItem>
            </Box>
          </Box>

          <Box
            display="flex"
            flex={1}
            justifyContent="center"
            alignItems="center"
            className="margin-top-lg print-margin-top"
          >
            <Box display="flex" flex={33}>
              <Card component="md-card" style={{ background: 'white', display: 'flex', flex: 100 }}>
                <Box
                  className="toolbar-div-print text-size-sm"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box component="span" display="flex"></Box>
                  Deal Balances
                  <Box component="span" display="flex"></Box>
                </Box>

                <Box className="statement-list-text">
                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Begin Principal Balance
                    <Box component="span" display="flex" flex={1}></Box>
                    {currency.format(beginPrincipalBalance ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Advances
                    <Box component="span" display="flex" flex={1}></Box>
                    {currency.format(totals?.advanceAmount ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Principal Payments
                    <Box component="span" display="flex" flex={1}></Box>
                    {currency.format(totals?.principalPaid ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Ending Principal Balance
                    <Box component="span" display="flex" flex={1}></Box>
                    {currency.format(headlines?.principalBalance ?? 0)}
                  </ListItem>
                </Box>
              </Card>
            </Box>

            <Box display="flex" flex={33}>
              <Card component="md-card" style={{ background: 'white', display: 'flex', flex: 100 }}>
                <Box className="toolbar-div-print text-size-sm" display="flex" layout-align="center center">
                  <Box component="span" display="flex" />
                  Payment Summary
                  <Box component="span" display="flex" />
                </Box>

                <Box className="statement-list-text">
                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Total Payments
                    <Box component="span" display="flex" flex={1}></Box>
                    {currency.format(totals?.paymentAmount ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Fees Paid
                    <Box component="span" display="flex" flex={1}></Box>
                    {currency.format(totals?.feesPaid ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Interest Paid
                    <Box component="span" display="flex" flex={1}></Box>
                    {currency.format(totals?.interestPaid ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Principal Paid
                    <Box component="span" display="flex" flex={1}></Box>
                    {currency.format(totals?.principalPaid ?? 0)}
                  </ListItem>
                </Box>
              </Card>
            </Box>

            <Box display="flex" flex={33}>
              <Card component="md-card" style={{ background: 'white', display: 'flex', flex: 100 }}>
                <Box
                  className="toolbar-div-print text-size-sm"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box component="span" display="flex" />
                  Account Summary
                  <Box component="span" display="flex" />
                </Box>

                <Box className="statement-list-text">
                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Asset Value
                    <Box component="span" display="flex" flex={1}></Box>
                    {currency.format(headlines?.assetValue ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Total Credit
                    <Box component="span" display="flex" flex={1}></Box>
                    {currency.format(headlines?.totalAvailableCredit ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Principal Balance
                    <Box component="span" display="flex" flex={1}></Box>
                    {currency.format(headlines?.principalBalance ?? 0)}
                  </ListItem>

                  <Divider />

                  <ListItem disablePadding sx={{ height: rowHeight, padding: '0 16px 0 16px' }}>
                    Available Credit
                    <Box component="span" display="flex" flex={1}></Box>
                    {currency.format(headlines?.currentAvailableCredit ?? 0)}
                  </ListItem>
                </Box>
              </Card>
            </Box>
          </Box>

          <Card
            component="md-card"
            // className="margin-top-md"
            style={{ background: 'white', display: 'flex', flex: 100, marginTop: '25px' }}
          >
            <Box className="toolbar-div-print text-size-sm" display="flex" justifyContent="center" alignItems="center">
              <Box component="span" display="flex" />
              Transaction Activity ({dayjs(startDate).format(shortDate)} - {dayjs(endDate).format(shortDate)})
              <Box component="span" display="flex" />
            </Box>

            <Divider />

            {!!listData.length && (
              <Box bgcolor="white" className="fadeDown">
                <ListHeader
                  items={[
                    { propName: '__t', label: 'Type' },
                    { propName: 'sequenceNumber', label: 'Effective Date' },
                    { propName: 'advanceAmount', label: 'Advance Amount', textAlign: 'right' },
                    { propName: 'feesCharged', label: 'Fees Charged', textAlign: 'right' },
                    { propName: 'paymentAmount', label: 'Payment Amount', textAlign: 'right' },
                    { propName: 'appliedToFees', label: 'Fees Paid', textAlign: 'right' },
                    { propName: 'appliedToInterest', label: 'Interest Paid', textAlign: 'right' },
                    { propName: 'appliedToPrincipal', label: 'Principal Paid', textAlign: 'right' },
                    { propName: 'newPrincipalBalance', label: 'Principal Balance', textAlign: 'right' },
                  ]}
                  sortType={sortType}
                  sortReverse={sortReverse}
                  sortList={sortList}
                  hideSortIcon
                  padForScrollbar={listData.length > 11}
                />
                <Divider />
                <Box
                  // component="md-content"
                  // className={data.length > 11 ? 'fixed-list-lg' : ''}
                  sx={{ maxHeight: `${(11 * rowHeight).toString()}px` }}
                >
                  {listData.map((transaction, i) => (
                    <ListRow
                      key={transaction._id}
                      padForScrollbar={listData.length > 11}
                      items={[
                        {
                          propName: '__t',
                          label: 'Type',
                          icon: (
                            <>
                              {transaction.__t === DataModelPluralType.Advance && (
                                <Tooltip title="Advance">
                                  <Payment sx={{ fontSize: '1.9em' }} />
                                </Tooltip>
                              )}
                              {transaction.__t === 'payments' && (
                                <Tooltip title="Payment">
                                  <LocalAtm sx={{ fontSize: '1.9em' }} />
                                </Tooltip>
                              )}
                              {transaction.__t === DataModelPluralType.Statement && (
                                <Tooltip title="Statement">
                                  <Assignment sx={{ fontSize: '1.9em' }} />
                                </Tooltip>
                              )}
                            </>
                          ),
                        },
                        {
                          propName: 'sequenceNumber',
                          label: 'Effective Date',
                          content: dayjs(transaction.effectiveDate).format(mediumDate),
                        },
                        {
                          propName: 'advanceAmount',
                          label: 'Advance Amount',
                          content:
                            transaction.__t === DataModelPluralType.Advance &&
                            currency.format(transaction.advanceAmount ?? 0),
                          url: `/${transaction.__t}/${transaction._id}`,
                          textAlign: 'right',
                        },
                        {
                          propName: 'feesCharged',
                          label: 'Fees Charged',
                          content:
                            transaction.__t !== DataModelPluralType.Statement &&
                            currency.format(transaction.originationFee ?? 0),
                          url: `/${transaction.__t}/${transaction._id}`,
                          textAlign: 'right',
                        },
                        {
                          propName: 'paymentAmount',
                          label: 'Payment Amount',
                          content:
                            transaction.__t === DataModelPluralType.Payment &&
                            currency.format(transaction.paymentAmount ?? 0),
                          url: `/${transaction.__t}/${transaction._id}`,
                          textAlign: 'right',
                        },
                        {
                          propName: 'appliedToFees',
                          label: 'Fees Paid',
                          content:
                            transaction.__t === DataModelPluralType.Payment &&
                            currency.format(transaction.appliedToFees ?? 0),
                          textAlign: 'right',
                        },
                        {
                          propName: 'appliedToInterest',
                          label: 'Interest Paid',
                          content:
                            transaction.__t === DataModelPluralType.Payment &&
                            currency.format(transaction.appliedToInterest ?? 0),
                          textAlign: 'right',
                        },
                        {
                          propName: 'appliedToPrincipal',
                          label: 'Principal Paid',
                          content:
                            transaction.__t === DataModelPluralType.Payment &&
                            currency.format(transaction.appliedToPrincipal ?? 0),
                          textAlign: 'right',
                        },
                        {
                          propName: 'newPrincipalBalance',
                          label: 'Principal Balance',
                          content: currency.format(
                            (transaction.__t === DataModelPluralType.Statement
                              ? transaction.principalBalance
                              : transaction.newPrincipalBalance) ?? 0
                          ),
                          textAlign: 'right',
                        },
                      ]}
                      rowIndex={i}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box hidden className="fadeDown">
              {/* TRANSACTIONS LIST HEADER */}
              <ListHeader
                items={[
                  { propName: '__t', label: 'Type' },
                  { propName: 'sequenceNumber', label: 'Effective Date' },
                  { propName: 'advanceAmount', label: 'Advance Amount', textAlign: 'right' },
                  { propName: 'feesCharged', label: 'Fees Charged', textAlign: 'right' },
                  { propName: 'paymentAmount', label: 'Payment Amount', textAlign: 'right' },
                  { propName: 'appliedToFees', label: 'Fees Paid', textAlign: 'right' },
                  { propName: 'appliedToInterest', label: 'Interest Paid', textAlign: 'right' },
                  { propName: 'appliedToPrincipal', label: 'Principal Paid', textAlign: 'right' },
                  { propName: 'newPrincipalBalance', label: 'Principal Balance', textAlign: 'right' },
                ]}
                sortType={sortType}
                sortReverse={sortReverse}
                sortList={sortList}
                hideSortIcon
              />

              <Divider />

              {/* TRANSACTIONS */}
              <Box component="md-content">
                {listData
                  .filter((transaction) => transaction.__t !== DataModelPluralType.Adjustment)
                  .map((transaction, i) => (
                    <ListItem
                      key={transaction._id}
                      className={`capitalize row-height-${rowHeight} ${i % 2 ? '' : 'highlighted'}`}
                    >
                      <Box
                        display="flex"
                        flex="100"
                        justifyContent="space-around"
                        alignItems="center"
                        className="text-center md-subhead-sm-print"
                      >
                        {/* TYPE */}
                        <Box component="span" width="100%">
                          {transaction.__t === DataModelPluralType.Advance && (
                            <Tooltip title="Advance">
                              <Payment sx={{ fontSize: '1.9em' }} />
                            </Tooltip>
                          )}
                          {transaction.__t === 'payments' && (
                            <Tooltip title="Payment">
                              <LocalAtm sx={{ fontSize: '1.9em' }} />
                            </Tooltip>
                          )}
                          {transaction.__t === DataModelPluralType.Statement && (
                            <Tooltip title="Statement">
                              <Assignment sx={{ fontSize: '1.9em' }} />
                            </Tooltip>
                          )}
                        </Box>

                        {/* EFFECTIVE DATE */}
                        <ListDataColumn
                          label="Effective Date"
                          value={dayjs(transaction.effectiveDate).format(mediumDate)}
                        />

                        {/* ADVANCE AMOUNT */}
                        <ListDataColumn
                          label="Advance Amount"
                          value={
                            transaction.__t === DataModelPluralType.Advance &&
                            currency.format(transaction.advanceAmount ?? 0)
                          }
                          url={`/${transaction.__t}/${transaction._id}`}
                          textAlign="right"
                        />

                        {/* FEES CHARGED */}
                        <ListDataColumn
                          label="Fees Charged"
                          value={
                            transaction.__t !== DataModelPluralType.Statement &&
                            currency.format(transaction.originationFee ?? 0)
                          }
                          url={`/${transaction.__t}/${transaction._id}`}
                          textAlign="right"
                        />

                        {/* <Box component="span" display="flex" className="text-right">
                          <a
                            ng-hide="transaction.__t == DataModelPluralType.Statement"
                            ng-href="#!/{{transaction.__t}/{{transaction._id}}"
                          >
                            {transaction.__t !== DataModelPluralType.Statement &&
                              currency.format(transaction.originationFee ?? 0)}
                          </a>
                        </Box> */}

                        {/* PAYMENT AMOUNT */}
                        <ListDataColumn
                          label="Payment Amount"
                          value={
                            transaction.__t === DataModelPluralType.Payment &&
                            currency.format(transaction.paymentAmount ?? 0)
                          }
                          url={`/${transaction.__t}/${transaction._id}`}
                          textAlign="right"
                        />

                        {/* <Box component="span" display="flex" className="text-right">
                          <a
                            ng-hide="transaction.__t == DataModelPluralType.Statement"
                            ng-href="#!/{{transaction.__t}/{{transaction._id}}"
                          >
                            {transaction.__t === DataModelPluralType.Payment &&
                              currency.format(transaction.paymentAmount ?? 0)}
                          </a>
                        </Box> */}

                        {/* FEES PAID */}
                        <ListDataColumn
                          label="Fees Paid"
                          value={
                            transaction.__t === DataModelPluralType.Payment &&
                            currency.format(transaction.appliedToFees ?? 0)
                          }
                          textAlign="right"
                        />

                        {/* <Box component="span" display="flex" className="text-right">
                          {transaction.__t === DataModelPluralType.Payment &&
                            currency.format(transaction.appliedToFees ?? 0)}
                        </Box> */}

                        {/* INTEREST PAID */}
                        <ListDataColumn
                          label="Interest Paid"
                          value={
                            transaction.__t === DataModelPluralType.Payment &&
                            currency.format(transaction.appliedToInterest ?? 0)
                          }
                          textAlign="right"
                        />
                        {/* 
                        <Box component="span" display="flex" className="text-right">
                          {transaction.__t === DataModelPluralType.Payment &&
                            currency.format(transaction.appliedToInterest ?? 0)}
                        </Box> */}

                        {/* INTEREST DUE */}
                        <ListDataColumn
                          label="Interest Due"
                          value={
                            transaction.__t === DataModelPluralType.Payment &&
                            currency.format(transaction.appliedToPrincipal ?? 0)
                          }
                          textAlign="right"
                        />

                        {/* <Box component="span" display="flex" className="text-right">
                          {transaction.__t === DataModelPluralType.Payment &&
                            currency.format(transaction.appliedToPrincipal ?? 0)}
                        </Box> */}

                        <Box component="span" className="divider-bar" />

                        {/* PRINCIPAL BALANCE */}
                        <ListDataColumn
                          label="Principal Balance"
                          value={currency.format(
                            (transaction.__t === DataModelPluralType.Statement
                              ? transaction.principalBalance
                              : transaction.newPrincipalBalance) ?? 0
                          )}
                          textAlign="right"
                        />

                        {/* <Box component="span" display="flex" className="text-right">
                          {currency.format(
                            (transaction.__t === DataModelPluralType.Statement
                              ? transaction.principalBalance
                              : transaction.newPrincipalBalance) ?? 0
                          )}
                        </Box> */}
                      </Box>
                      <Divider />
                    </ListItem>
                  ))}
              </Box>

              {transactionHistory && transactionHistory?.length === 0 && (
                <ListItem sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Box component="span" display="flex" />

                  <Box className="md-warn">No transactions found for this period.</Box>

                  <Box component="span" display="flex" />
                </ListItem>
              )}
            </Box>
          </Card>
        </Box>
      </section>
    </section>
  );
});
